import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "leaflet/dist/leaflet.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import OverviewSection from "./OverviewSection";
import FinancialAnalysis from "./FinancialAnalysis";
import ReportsAndAlerts from "./ReportsAndAlerts";
import DonutAndEntries from "./DonutAndEntries";
import LineAndBarEmission from "./LineAndBarEmission";
import SinkGraphs from "./SinkGraphs";
import EmissionsPerTon from "./EmissionsPerTon";
import { useEffect,useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ChatAssistant from "./ChatAssistant";
import NeutralityGraph from "./NeutralityGraph";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function DashBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fetchWeekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState(null); // To store calculated totals
  const [carbonFootprintsChecked, setCarbonFootprintsChecked] = useState(false);
  const [allCarbonFootprintsEntered, setAllCarbonFootprintsEntered] = useState(false);
  const [carbonFootprintDetails, setCarbonFootprintDetails] = useState({});
  const [checkingCarbonFootprints, setCheckingCarbonFootprints] = useState(true);

  // Check if all carbon footprints have been entered for today
  useEffect(() => {
    const checkCarbonFootprints = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        // Add a timeout to the request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await axios.get(`${API_BASE_URL}/api/carbon-footprints/check`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        setAllCarbonFootprintsEntered(response.data.allDataEntered);
        setCarbonFootprintDetails(response.data.details);
        setCarbonFootprintsChecked(true);
        setCheckingCarbonFootprints(false);
      } catch (err) {
        console.error("Error checking carbon footprints:", err);
        // In case of error (timeout, network issue, etc.), we still want to show the dashboard
        // but with a warning that we couldn't verify the data
        setAllCarbonFootprintsEntered(false);
        setCarbonFootprintDetails({
          electricity: false,
          fuelCombustion: false,
          shipping: false,
          explosion: false
        });
        setCarbonFootprintsChecked(true);
        setCheckingCarbonFootprints(false);
      }
    };

    checkCarbonFootprints();
  }, []);

  useEffect(() => {
    const fetchLastWeekData = async () => {
      try {
        setLoading(true);

        // Get today's date
        const today = new Date();

        // Calculate the start of the current week (this week's Monday)
        const currentWeekMonday = new Date(today);
        currentWeekMonday.setDate(today.getDate() - today.getDay() + 1);
        currentWeekMonday.setHours(0, 0, 0, 0);

        // Calculate the start and end of last week
        const lastWeekMonday = new Date(currentWeekMonday);
        lastWeekMonday.setDate(currentWeekMonday.getDate() - 7);
        const lastWeekSunday = new Date(lastWeekMonday);
        lastWeekSunday.setDate(lastWeekMonday.getDate() + 6);
        lastWeekSunday.setHours(23, 59, 59, 999);

        // Format the dates
        const formattedStartDate = lastWeekMonday.toISOString().split("T")[0];
        const formattedEndDate = lastWeekSunday.toISOString().split("T")[0];

        // Make the API call with timeout
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await axios.get(
          `${API_BASE_URL}/api/data/${formattedStartDate}/${formattedEndDate}`,
          { signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        setWeekData(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch data");
        setLoading(false);
        console.error(err);
      }
    };

    // Only fetch data if carbon footprints have been checked
    if (carbonFootprintsChecked) {
      fetchLastWeekData();
    }
  }, [carbonFootprintsChecked]);

  // Calculate totals when `fetchWeekData` updates
  const [totalEmissions, setTotalEmissions] = useState({
    electricity: 0,
    fuelCombustion: 0,
    shipping: 0,
    explosion: 0
  });

  useEffect(() => {
    if (!fetchWeekData) return;

    // Calculate total CO2 emissions for each field
    const emissions = {
      electricity: fetchWeekData.electricity.reduce((total, item) => {
        return total + (item.result.CO2?.value || 0);
      }, 0),
      fuelCombustion: fetchWeekData.fuelCombustion.reduce((total, item) => {
        return total + (item.result.CO2?.value || 0);
      }, 0),
      shipping: fetchWeekData.shipping.reduce((total, item) => {
        return total + (parseFloat(item.result.carbonEmissions.kilograms) || 0);
      }, 0),
      explosion: fetchWeekData.explosion.reduce((total, item) => {
        return total + (parseFloat(item.emissions.CO2) || 0);
      }, 0)
    };

    setTotalEmissions(emissions);
  }, [fetchWeekData]);
  // console.log("total emission",totalEmissions);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the current timestamp using Date.now()
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        
        // Make the API call with timeout
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await axios.get(`${API_BASE_URL}/api/data/${formattedDate}`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        setData(response.data);
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      }
    };

    // Only fetch data if carbon footprints have been checked
    if (carbonFootprintsChecked) {
      fetchData();
    }
  }, [carbonFootprintsChecked]); // Empty dependency array ensures it runs only on page load.

  useEffect(() => {
    // console.log(data); // Log the data after it is updated
  }, [data]);

  // Show loading state while checking carbon footprints
  if (checkingCarbonFootprints) {
    return (
      <div className="bg-gray-900 text-white min-h-screen w-full overflow-x-hidden flex flex-col">
        <Navbar className="mb-2 pt-4" />
        <ChatAssistant />
        
        {/* Centered loading animation */}
        <div className="flex-grow flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mb-4"></div>
          <p className="text-xl font-semibold">Checking carbon footprints...</p>
        </div>
        
        <Footer className="w-full bg-gray-800 text-white py-4 text-center" />
      </div>
    );
  }

  // Show warning if not all carbon footprints have been entered
  if (carbonFootprintsChecked && !allCarbonFootprintsEntered) {
    return (
      <div className="bg-gray-900 text-white min-h-screen w-full overflow-x-hidden">
        <Navbar className="mb-2 pt-4" />
        <ChatAssistant />
        
        <div className="px-10 py-10">
          <div className="bg-yellow-900 border-l-4 border-yellow-500 text-yellow-100 p-4" role="alert">
            <div className="flex">
              <div className="py-1">
                <svg className="fill-current h-6 w-6 text-yellow-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M10 20a10 10 0 1 1 0-20 10 10 0 0 1 0 20zm-1-15h2v6h-2V5zm0 8h2v2h-2v-2z"/>
                </svg>
              </div>
              <div>
                <p className="font-bold text-xl">Incomplete Carbon Footprint Data</p>
                <p className="text-lg mt-2">
                  Please enter today's carbon footprint data for all categories:
                </p>
                <ul className="mt-4 space-y-2">
                  <li className={`flex items-center ${carbonFootprintDetails.electricity ? 'text-green-400' : 'text-red-400'}`}>
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      {carbonFootprintDetails.electricity ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    Electricity Consumption
                  </li>
                  <li className={`flex items-center ${carbonFootprintDetails.fuelCombustion ? 'text-green-400' : 'text-red-400'}`}>
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      {carbonFootprintDetails.fuelCombustion ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    Fuel Combustion Emissions
                  </li>
                  <li className={`flex items-center ${carbonFootprintDetails.shipping ? 'text-green-400' : 'text-red-400'}`}>
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      {carbonFootprintDetails.shipping ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    Shipping Emissions
                  </li>
                  <li className={`flex items-center ${carbonFootprintDetails.explosion ? 'text-green-400' : 'text-red-400'}`}>
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      {carbonFootprintDetails.explosion ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    Explosion Emissions
                  </li>
                </ul>
                <p className="mt-4 text-lg">
                  Please navigate to the emissions page to enter the missing data.
                </p>
                <div className="mt-6">
                  <button 
                    onClick={() => navigate('/emission')}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-300"
                  >
                    Go to Emissions Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer className="w-full bg-gray-800 text-white py-4 text-center" />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;  // Show error message if data fetch fails
  }

  if (!data) {
    return <div>Loading...</div>;  // Show loading message if data is still null
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen w-full overflow-x-hidden">
      <Navbar className="mb-2 pt-4" />
      <ChatAssistant />

      {/* Dashboard Grid Layout */}
      <div className="px-10">
        <OverviewSection />
      <div className="px-10 mt-10 w-full ">  
        <EmissionsPerTon />
      </div>
      </div>
      <div className="grid grid-cols-1 gap-8 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
       <DonutAndEntries data={data}/>

          {/* Tabs */}
          <div className="flex flex-wrap gap-8 p-1 justify-between xl:col-span-3">
          <div className="flex flex-col bg-gray-800 rounded-lg shadow-md p-6 text-center flex-1">
            <h2 className="text-lg font-bold mb-2">Electricity</h2>
            <p className="text-3xl font-semibold mb-2">{totalEmissions.electricity} MWh</p>
            <p className="text-red-500">+17.0% from last week</p>
          </div>

          <div className="flex flex-col bg-gray-800 rounded-lg shadow-md p-6 text-center flex-1">
            <h2 className="text-lg font-bold mb-2">Explosion</h2>
            <p className="text-3xl font-semibold mb-2">{totalEmissions.explosion}  tCO2e</p>
            <p className="text-green-500">-5.8% from last week</p>
          </div>

          <div className="flex flex-col bg-gray-800 rounded-lg shadow-md p-6 text-center flex-1">
            <h2 className="text-lg font-bold mb-2">Fuel</h2>
            <p className="text-3xl font-semibold mb-2">{totalEmissions.fuelCombustion} tCO2e</p>
            <p className="text-red-500">+11.4% from last week</p>
          </div>

          <div className="flex flex-col bg-gray-800 rounded-lg shadow-md p-6 text-center flex-1">
            <h2 className="text-lg font-bold mb-2">Shipping</h2>
            <p className="text-3xl font-semibold mb-2">{totalEmissions.shipping} tCO2e</p>
            <p className="text-green-500">-8.7% from last week</p>
          </div>
        </div>

       
{/* Emission Line and Bar Chart Below Doughnut and Data Entries */}

<LineAndBarEmission data={data}/>
<SinkGraphs />
<div className="flex justify-center w-screen ">
<NeutralityGraph />
</div>

        <div className="flex flex-col xl:flex-row gap-8 xl:col-span-3 p-4">
    <FinancialAnalysis />
    <ReportsAndAlerts />
</div>


      </div>
      <Footer className="w-full bg-gray-800 text-white py-4 text-center" />
    </div>
  );
}

export default DashBoard;